import { useCallback, useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { baseApi } from "@/shared/api";
import { useAppDispatch } from "@/shared/hooks";
import { useGetSigningOperationQuery } from "../api/mobileSigning";
import {
  FINALIZATION_TIMEOUT_MS,
  FINALIZING_POLL_INTERVAL,
  POST_SIGNING_TAGS,
  getBackoffInterval,
  withPollJitter,
} from "../consts/postSigning";
import { mapSigningV2Error, getSigningV2ErrorCode } from "../utils/mapSigningV2Error";
import type { MobileLaunch, SignerRoleV2, SigningOperationWithLinks } from "../types/mobileSigning";
import {
  deriveEgovPhase,
  getEgovPollInterval,
  isEgovLaunchExpired,
  isTerminalEgovPhase,
  pickEgovLink,
  type EgovPhase,
} from "./egovPhase";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

interface UseEgovMobileSigningOptions {
  launchFn: () => Promise<MobileLaunch | SigningOperationWithLinks>;
  /** Start a fresh session on a document that is already registered. */
  resumeFn?: () => Promise<MobileLaunch | SigningOperationWithLinks>;
  cancelFn?: (operationPublicId: string) => Promise<unknown>;
  onSuccess: () => void;
  setPrompt: (prompt: PromptState) => void;
  t: TFunction;
  onSessionChange?: (isActive: boolean) => void;
  /** Which eGov app to offer before the provider reports the signer role. */
  preferBusiness?: boolean;
}

interface LaunchState {
  operationPublicId: string;
  qrCode: string;
  primaryLink: string;
  secondaryLink: string;
  isBusinessLink: boolean;
  expireAt: number | null;
  isPending: boolean;
}

const DEFAULT_POLL_INTERVAL = 3000;

export function useEgovMobileSigning(options: UseEgovMobileSigningOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const dispatch = useAppDispatch();

  const [launch, setLaunch] = useState<LaunchState | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const startGuardRef = useRef(false);
  const finishedRef = useRef(false);
  const failureCountRef = useRef(0);
  const hasAcceptedSignatureRef = useRef(false);

  const [pollInterval, setPollInterval] = useState(DEFAULT_POLL_INTERVAL);

  const rawIsExpired = isEgovLaunchExpired(launch?.expireAt ?? null, now);
  const isExpired = rawIsExpired && !hasAcceptedSignatureRef.current;

  const {
    data: operation,
    isError: isPollError,
    refetch,
  } = useGetSigningOperationQuery(launch?.operationPublicId as string, {
    // An expired session cannot change any more, and a hidden tab is nobody
    // watching: both used to keep hitting SIGEX every three seconds.
    skip: !launch || isExpired,
    pollingInterval: pollInterval,
    skipPollingIfUnfocused: true,
  });

  const phase: EgovPhase = deriveEgovPhase(
    operation ?? null,
    isStarting,
    isExpired,
    Boolean(launch?.isPending),
  );
  const isFinalizing = phase === "finalizing";
  const isActive = Boolean(launch) && !isTerminalEgovPhase(phase);
  const launchState: "idle" | "pending" | "ready" = !launch
    ? "idle"
    : launch.isPending
      ? "pending"
      : "ready";

  useEffect(
    function lockInAcceptedSignature() {
      if (isFinalizing) hasAcceptedSignatureRef.current = true;
    },
    [isFinalizing],
  );

  useEffect(
    function followServerPollCadence() {
      if (isPollError) {
        failureCountRef.current += 1;
        setPollInterval(getBackoffInterval(failureCountRef.current));
        return;
      }
      if (!operation) return;
      failureCountRef.current = 0;
      if (isFinalizing) {
        setPollInterval(withPollJitter(FINALIZING_POLL_INTERVAL));
        return;
      }
      setPollInterval(withPollJitter(getEgovPollInterval(operation)));
    },
    [operation, isPollError, isFinalizing],
  );

  useEffect(
    function refreshOnReturn() {
      if (!launch) return;
      function handleVisibility() {
        if (document.visibilityState === "visible") refetch();
      }
      document.addEventListener("visibilitychange", handleVisibility);
      return function cleanup() {
        document.removeEventListener("visibilitychange", handleVisibility);
      };
    },
    [launch, refetch],
  );

  const reset = useCallback(function reset() {
    setLaunch(null);
    setIsCancelling(false);
    startGuardRef.current = false;
    finishedRef.current = false;
    failureCountRef.current = 0;
    hasAcceptedSignatureRef.current = false;
  }, []);

  useEffect(
    function notifySession() {
      optionsRef.current.onSessionChange?.(isActive);
    },
    [isActive],
  );

  useEffect(
    function tickCountdown() {
      if (!isActive || !launch?.expireAt) return;
      setNow(Date.now());
      const timer = setInterval(() => setNow(Date.now()), 1000);
      return function cleanup() {
        clearInterval(timer);
      };
    },
    [isActive, launch?.expireAt],
  );

  useEffect(
    function reactToTerminalPhase() {
      if (!launch || finishedRef.current) return;
      if (!isTerminalEgovPhase(phase)) return;

      finishedRef.current = true;

      if (phase === "done") {
        reset();
        dispatch(baseApi.util.invalidateTags([...POST_SIGNING_TAGS]));
        optionsRef.current.onSuccess();
        return;
      }

      if (phase === "settlement_failed") {
        dispatch(baseApi.util.invalidateTags([...POST_SIGNING_TAGS]));
        setErrorCode("settlement_failed");
        optionsRef.current.setPrompt({
          title: optionsRef.current.t("signing.finalizing.failedTitle"),
          text: optionsRef.current.t("signing.finalizing.failed"),
          variant: "error",
        });
        reset();
        return;
      }

      const providerCode = operation?.error?.code ?? null;
      setErrorCode(providerCode);
      const messageKey = providerCode
        ? `signing.egov.errors.${providerCode}`
        : phase === "canceled"
          ? "signing.egov.canceled"
          : "signing.egov.failed";
      const fallback = phase === "canceled" ? "signing.egov.canceled" : "signing.egov.failed";
      const message = optionsRef.current.t(messageKey);
      setError(message === messageKey ? optionsRef.current.t(fallback) : message);
      reset();
    },
    [phase, launch, operation, dispatch, reset],
  );

  useEffect(
    function capFinalizationWait() {
      if (!isFinalizing || finishedRef.current) return;
      const timer = setTimeout(function giveUpWaiting() {
        finishedRef.current = true;
        dispatch(baseApi.util.invalidateTags([...POST_SIGNING_TAGS]));
        optionsRef.current.setPrompt({
          title: optionsRef.current.t("signing.finalizing.title"),
          text: optionsRef.current.t("signing.finalizing.timeout"),
          variant: "success",
        });
        reset();
        optionsRef.current.onSuccess();
      }, FINALIZATION_TIMEOUT_MS);
      return function cleanup() {
        clearTimeout(timer);
      };
    },
    [isFinalizing, dispatch, reset],
  );

  const applyLaunch = useCallback(function applyLaunch(
    result: MobileLaunch | SigningOperationWithLinks,
  ) {
    const signerRole = (result as { signer_role?: SignerRoleV2 }).signer_role;
    const links = pickEgovLink(
      result,
      signerRole ?? (optionsRef.current.preferBusiness ? "organization" : undefined),
    );
    const isPending =
      (result as MobileLaunch).launch_state === "pending"
      || (!result.qr_code && !links.primary && !links.secondary);
    setLaunch({
      operationPublicId: result.operation_public_id,
      qrCode: result.qr_code,
      primaryLink: links.primary,
      secondaryLink: links.secondary,
      isBusinessLink: links.isBusiness,
      expireAt: result.expire_at ?? null,
      isPending,
    });
    finishedRef.current = false;
    failureCountRef.current = 0;
    hasAcceptedSignatureRef.current = false;
    setPollInterval(DEFAULT_POLL_INTERVAL);
    setNow(Date.now());
  }, []);

  const runLaunch = useCallback(
    async function runLaunch(fn: () => Promise<MobileLaunch | SigningOperationWithLinks>) {
      if (startGuardRef.current) return;
      startGuardRef.current = true;
      setIsStarting(true);
      setError(null);
      setErrorCode(null);

      try {
        applyLaunch(await fn());
      } catch (err: unknown) {
        startGuardRef.current = false;
        setErrorCode(getSigningV2ErrorCode(err));
        optionsRef.current.setPrompt({
          title: optionsRef.current.t("signing.egov.errors.startFailedTitle"),
          text: mapSigningV2Error(err, optionsRef.current.t, "signing.egov.startFailed"),
          variant: "error",
        });
      } finally {
        setIsStarting(false);
      }
    },
    [applyLaunch],
  );

  const handleStart = useCallback(
    function handleStart() {
      return runLaunch(optionsRef.current.launchFn);
    },
    [runLaunch],
  );

  const handleRetry = useCallback(
    function handleRetry() {
      const resume = optionsRef.current.resumeFn ?? optionsRef.current.launchFn;
      startGuardRef.current = false;
      finishedRef.current = false;
      hasAcceptedSignatureRef.current = false;
      setLaunch(null);
      return runLaunch(resume);
    },
    [runLaunch],
  );

  const handleCancel = useCallback(
    async function handleCancel() {
      if (!launch) return;
      setIsCancelling(true);
      try {
        await optionsRef.current.cancelFn?.(launch.operationPublicId);
      } catch {
        // The poll reports the real state; a failed cancel changes nothing.
      }
      reset();
    },
    [launch, reset],
  );

  return {
    phase,
    launchState,
    isActive,
    isStarting,
    isCancelling,
    isExpired,
    isFinalizing,
    error,
    errorCode,
    operation: operation ?? null,
    operationPublicId: launch?.operationPublicId ?? null,
    qrCode: launch?.qrCode ?? null,
    primaryLink: launch?.primaryLink ?? null,
    secondaryLink: launch?.secondaryLink ?? null,
    isBusinessLink: launch?.isBusinessLink ?? Boolean(options.preferBusiness),
    expireAt: launch?.expireAt ?? null,
    canCancel: Boolean(operation?.capabilities?.cancel),
    canRetry: Boolean(operation?.capabilities?.retry) || Boolean(optionsRef.current.resumeFn),
    now,
    handleStart,
    handleRetry,
    handleCancel,
    reset,
    clearError: () => setError(null),
  };
}
